<template>
  <transition name="modal">
    <div class="fts-modal" 
      tabindex="0"
      :class="{
        'scrollable': scrollable, 
        'full-modal': fullScreen,
        'center': placementCenter
      }" 
      @mousedown.self.prevent="backdropClose"
      @focus="focus"
      @blur="blur"
      ref="ftsModal">
      <!-- Modal Wrapper -->
      <div 
        class="fts-modal-wrapper" 
        :style="wrapperStyle" 
        :class="[{ 'persistent': persistentWarn }]"
        @mousedown.self.prevent="backdropClose">
        <div class="fts-modal-container solid-style" :class="appendContainerClass">
          <!-- Header -->
          <div class="fts-modal-header" :class="headerClass" v-if="header">         
            <slot name="header" :defaultClass="'default-header-title'">
              <div class="default-header-title">{{title}}</div>
            </slot>
          </div>     
          <!-- Body -->
          <div class="fts-modal-body fts-scrollbar" :class="contentClass">
            <slot name="body">{{content}}</slot>
          </div>
          <!-- Footer -->
          <div class="fts-modal-footer" :class="footerClass" v-if="footer">
            <slot name="footer">
              <div class="default-action-container">
                <button type="button" class="fts-btn fts-btn-text fts-btn-default fts-btn-icon-mr fts-btn-sm" @click="close" v-if="defaultButton"> 
                  <font-awesome-icon :icon="[closeIconStyle, closeIconName]" fixed-width />
                  {{closeText}}
                </button>
                <slot name="footerButton"></slot>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
window.onhashchange = function() {
  if (document.documentElement.classList.contains('no-scroll')) 
    document.documentElement.classList.remove('no-scroll')
}

export default {
  props: {
    appendToBody: Boolean,
    fullScreen: Boolean,
    placement: String,
    width: {
      type: String,
      default: '600px'
    },
    backdrop: {
      type: Boolean,
      default: true
    },
    escapeEvent: {
      type: Boolean,
      default: true,
    },
    /* prevent close from backdrop or escape handler */
    persistent: Boolean,
    scrollable: {
      type: Boolean,
      default: true
    },
    appendContainerClass: String,
    header: {
      type: Boolean,
      default: true
    },
    headerClass: {
      type: String,
      default: 'default'
    },
    title: {
      type: String,
      default: 'Default Header'
    },
    content: {
      type: String,
      default: 'Default Content'
    },
    contentClass: {
      type: String,
      default: 'default'
    },
    footer: {
      type: Boolean,
      default: true
    },
    footerClass: {
      type: String,
      default: 'default'
    },
    defaultButton: {
      type: Boolean,
      default: true
    },
    closeText: {
      type: String,
      default: 'Close'
    },
    closeIcon: {
      type: Boolean,
      default: true
    },
    closeIconStyle: {
      type: String,
      default: 'fas'
    },
    closeIconName: {
      type: String,
      default: 'times'
    }
  },
  data: () => ({
    uid: null,
    persistentWarn: false
  }),
  methods: {
    modalExist(modal) {
      let modals = document.documentElement.getElementsByClassName(modal || 'fts-modal')
      return Array.prototype.filter.call(modals, x => x.className.indexOf('modal-leave') == -1) || []
    },
    // Start Scroll Event
    scrollListener(e) {
      let modals = document.documentElement.getElementsByClassName('fts-modal')
      let noModalExist = Array.prototype.filter.call(modals, x => x.className.indexOf('modal-leave') == -1).length ? false : true
      if (noModalExist) window.removeEventListener('wheel', this.scrollListener)
      if (this.overlay(e.target) || e.target === document.body || this.checkPath(e))
        e.preventDefault()
    },
    checkPath(e) {
      const path = e.path || this.composedPath(e)
      const deltaY = e.deltaY
      const deltaX = e.deltaX
      const shiftKey = e.shiftKey

      for (let index = 0; index < path.length; index++) {
        const el = path[index]

        if (el === document) return true
        if (el === document.documentElement) return true
        if (el === this.$refs.ftsModal) return true

        if (this.hasScrollbar(el))
          return this.shouldScroll(
            el, 
            deltaY, 
            deltaX, 
            shiftKey, 
            _.takeRight(path, path.length - (index + 1))
          )
      }
    },
    composedPath(e) {
      if (e.composedPath)
        return e.composedPath();

      const path = []
      let el = e.target
      while (el) {
        path.push(el)

        if (el.tagName === 'HTML') {
          path.push(document)
          path.push(window)
          return path
        }

        el = el.parentElement
      }
      return path
    },
    hasScrollbar (el) {
      if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;

      const elStyle = window.getComputedStyle(el);
      return (
        (['auto', 'scroll', 'overlay'].includes(elStyle.overflowY) && el.scrollHeight > el.clientHeight) ||
        (['auto', 'scroll', 'overlay'].includes(elStyle.overflowX) && el.scrollWidth > el.clientWidth)
      );
    },
    shouldScroll (el, deltaY, deltaX, shiftKey, path) {
      let preventScroll = false
      if (shiftKey) {
        if (el.scrollLeft === 0 && deltaX < 0)
          preventScroll = true
        else
          preventScroll = (el.scrollLeft + el.clientWidth === el.scrollWidth && deltaX > 0)
      } else {
        if (el.scrollTop === 0 && deltaY < 0)
          preventScroll = true
        else
          preventScroll = (el.scrollTop + el.clientHeight === el.scrollHeight && deltaY > 0) 
      }

      if (path && path.length > 1 && preventScroll)
        return this.checkPath({
          ...path[0],
          deltaX: deltaX,
          deltaY: deltaY,
          shiftKey: shiftKey,
          path: path,
        })
      else
        return preventScroll
    },
    overlay(element) {
      return element === this.$refs.ftsModal || element === this.$refs.modalMockElement
    },
    persistentNotice() {
      if (!this.persistentWarn) {
        this.persistentWarn = true
        setTimeout(() => {
          this.persistentWarn = false
        }, 500)
      }
    },
    // End Scroll Event
    backdropClose() {
      if (!this.backdrop || this.persistent)
        this.persistentNotice()
      else
        this.close()
    },
    close() {
      this.$emit('close')
    },
    focus() {
      this.$emit('focus')
    },
    blur() {
      this.$emit('blur')
    },
    // Event
    escapeHandler(e) {
      if (
        !this.persistent &&
        this.escapeEvent &&
        e.keyCode === 27
      ) {
        e.stopPropagation(); 
        this.close();
      }
      // if (e.keyCode === 27) {
      //   e.stopPropagation(); 
      //   if (!this.persistent)
      //     this.close();
      // }
    },
    windowEventEscapeHandler(e) {
      if (
        !this.persistent &&
        this.escapeEvent &&
        e.keyCode === 27 &&
        (
          e.target && (e.target.tagName || e.target.nodeName) === 'BODY'
        )
      ) {
        e.stopPropagation(); 
        this.close();
      }
      // if (e.keyCode === 27) {
      //   e.stopPropagation(); 
      //   if (
      //     (
      //       e.target && 
      //       (e.target.tagName || e.target.nodeName)
      //     ) === 'BODY' && 
      //     !this.persistent
      //   )
      //     this.close();
      // }
    },
  },
  computed: {
    wrapperStyle() {
      if (!this.fullScreen && this.width) 
        return {
          width: this.width
        }
    },
    documentScrollbarExist() {
      return window.innerWidth - document.documentElement.scrollWidth
    },
    placementCenter() {
      return this.placement === 'center'
    }
  },
  mounted() {
    window.addEventListener('keydown', this.windowEventEscapeHandler);
    window.addEventListener('wheel', this.scrollListener, { passive: false})    
    this.$refs.ftsModal.addEventListener('keydown', this.escapeHandler);
    
    if (this.documentScrollbarExist && this.fullScreen) 
      document.documentElement.classList.add('no-scroll')

    if (this.appendToBody)
      document.body.appendChild(this.$refs.ftsModal);

    this.$refs.ftsModal.focus();
    this.$emit('opened')
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.windowEventEscapeHandler);
    window.removeEventListener('wheel', this.scrollListener)
    this.$refs.ftsModal.removeEventListener('keydown', this.escapeHandler);  
    // Autofocus next modal
    const modalExist = [...this.modalExist()]
    if (modalExist.length) 
      modalExist.pop().focus()
    // Check full-modal exist
    const fullModalExist = this.modalExist('full-modal').length
    if (!fullModalExist && document.documentElement.classList.contains('no-scroll')) 
      document.documentElement.classList.remove('no-scroll')    
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

@keyframes shake {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }
  30%, 50%, 70% {
    transform: translate3d(-2px, 0, 0);
  }
  40%, 60% {
    transform: translate3d(1px, 0, 0);
  }
}

// transition
// .modal-enter-active {
//   transition: all .5s ease;
// }
// .modal-leave-active {
//   transition: all .5s ease;
// }
// .modal-enter, .modal-leave-to {
//   .fts-modal-wrapper {
//     transform: translateY(-100%);
//     opacity: .5;
//   }
// }

.fts-modal {
  $height: 55px;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: rgba(0, 0, 0, .25);
  cursor: default;
  z-index: $z-index-modal;
  transition: .3s cubic-bezier(0.25, 0.8, 0.25, 1);
  .dark & {
    background-color: rgba(0, 0, 0, .75);
  }
  &.scrollable {
    > .fts-modal-wrapper {
      max-height: 100%;
      > .fts-modal-container {
        max-height: 100%;
        > .fts-modal-body {
          flex: 1 1 100%;
          overflow-y: auto;
        }
      }
    }
    &:not(.full-modal) {
      > .fts-modal-wrapper {
        height: calc(100% - #{$height});
      }
    }
  }
  &:not(.full-modal) {
    &:not(.center) {
      > .fts-modal-wrapper {
        align-items: flex-start;
        margin-top: $height;
      }
    }
    &.center {
      justify-content: center;
      > .fts-modal-wrapper {
        align-items: center;
      }
    }
    > .fts-modal-wrapper {
      width: 600px;     
      &.persistent {
        animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
        transform: translate3d(0, 0, 0);
        backface-visibility: hidden;
        perspective: 1000px;
      }
      > .fts-modal-container {
        border-radius: .25rem;
      }
    }
  }
  &.full-modal {
    > .fts-modal-wrapper {
      height: 100%;
      width: 100%;
    }
  }
  > .fts-modal-wrapper {
    max-width: 100%;
    display: flex;
    transition: 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
    .fts-modal-container {
      position: relative;
      max-width: 100%;
      display: flex;
      flex: 1 1 100%;
      flex-direction: column;
      pointer-events: auto; 
      width: 100%;
      // box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12);
      box-shadow: var(--fts-elevation-3);
      background: var(--fts-background);
      color: var(--fts-text);
      z-index: inherit;
      &.solid-style {
        .fts-modal-header {
          &.default {
            background: $primaryColor;
            color:#fff;
          }
        }
      }
      &.light-style {
        .fts-modal-header {
          &.default-header {
            background-color: rgba($color: $primaryColor, $alpha: 0.05);
            color: $primaryColor;
          }
        }
      }
      .fts-modal-header {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
        &.default {
          padding: 1rem; 
          > .default-header-title {
            font-size: 1.2rem;
            font-weight: bold;      
            letter-spacing: .075em;
          }
        }
      }
      .fts-modal-body {
        &.default {
          padding: 1rem;
        }
      }
      .fts-modal-footer {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;

        &.default {
          padding: 12px 15px;
        }
        .default-action-container {
          text-align: right;
        }
      }
    }
  }
}

.no-scroll {
  overflow: hidden !important;
}

#gney_content #sidebar {
  left: 0;
}
</style>

<!-- Reference -->
<!-- Vuetify Dialog: MIT License -->
<!-- https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/mixins/overlayable/index.ts-->